import { ChevronLeftIcon } from '@heroicons/react/solid';
import { ExternalLinkIcon } from '@heroicons/react/outline';
import type { NextPage } from 'next';
import { NextSeo } from 'next-seo';
import { useRouter } from 'next/router';
import { useContext, useEffect, useState } from 'react';
import sanitizeHTML from 'sanitize-html';
import AppContext from '../../components/common/AppContext';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { getAllEvents, getEventById } from '../../lib/api/calendar-event';
import { CalendarEvent } from '../../lib/api/types/calendar-event';

interface Props {
  event: CalendarEvent;
}

const Veranstaltung: NextPage<Props> = ({ event }) => {
  const [redirect, setRedirect] = useState(false);
  const router = useRouter();
  const [isEventOrganizer, setIsEventOrganizer] = useState(false);

  const { setFilteredEvents } = useContext(AppContext);

  useEffect(() => {
    if (event?.category?.slug === 'angebote-fuer-veranstalter') {
      setIsEventOrganizer(true);
    }
  }, [event?.category?.slug]);

  useEffect(() => {
    if (redirect) {
      router.push('/');
    }

    setFilteredEvents([event]);
  }, [redirect, router, setFilteredEvents, event]);

  if (router.isFallback || redirect) {
    return <LoadingSpinner />;
  }

  if (!event && !redirect) {
    setRedirect(true);
    return <LoadingSpinner />;
  }

  const generalEventDetails = () => {
    return (
      <div className="bg-white shadow rounded-md mt-4">
        <div className="px-4 py-5 sm:p-6">
          <div className="border-b-2 border-dashed border-gray-200 pb-4 mb-6">
            <div className="flex justify-between items-center ">
              <h2 className="mr-2 text-2xl text-gray-800">Details</h2>
              <button
                onClick={() =>
                  window.open(
                    'https://docs.google.com/forms/d/e/1FAIpQLSc-QQWZm1CK1uK5HVhrqKVCFSWo1xjsL2x6HHpwoOp7OJOPag/viewform',
                    '_blank'
                  )
                }
                type="button"
                className="mr-2 sm:mr-4 opacity-90 inline-flex text-light items-center px-2 p-1 border border-transparent text-sm font-medium rounded-md text-white bg-primary shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                Kurs Bewerten
              </button>
            </div>
          </div>

          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Kursnummer:
            </div>
            <div className="font-light text-md text-gray-800">{event?.id}</div>
          </div>

          <div className={`py-1 my-3 ${event?.recurring ? 'hidden' : ''}`}>
            <div className="font-semibold text-md text-gray-800">
              Datum / Zeit:
            </div>
            <div className="font-light text-md text-gray-800">
              {event?.formattedDate}
            </div>
          </div>
          <div
            className={`py-1 my-3 ${!event?.additionalDates ? 'hidden' : ''}`}
          >
            <div className="font-semibold text-md text-gray-800">
              Zusätzliche Terminangaben:
            </div>
            <div className="font-light text-md text-gray-800 break-all">
              {event?.additionalDates}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Veranstaltungsort:
            </div>
            <div className="font-light text-md text-gray-800">
              {event?.venue}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Region:</div>
            <div className="font-light text-md text-gray-800">
              {event?.region}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Altersstufe:
            </div>
            <div className="font-light text-md text-gray-800">
              {event?.ageRange.replace(/\|/g, ' / ')}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Leitung:</div>
            <div className="font-light text-md text-gray-800">
              {event?.caretaker}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Kosten:</div>
            <div className="font-light text-md text-gray-800">
              {event?.price}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Veranstalter:
            </div>
            <div className="font-light text-md text-gray-800">
              {event?.organizer}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Auskunft:</div>
            <div className="font-light text-md text-gray-800">
              {event?.contactInformation}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Anmeldung:
            </div>
            <div className="font-light text-md text-gray-800 break-all">
              {event?.registrationInformation}
            </div>
          </div>
          {!!event.website && (
            <div className="py-1 my-3">
              <div className="font-semibold text-md text-gray-800">
                Website:
              </div>
              <div className="font-light text-md text-gray-800">
                <a
                  href={event.website}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center"
                >
                  <span className="mr-2 break-all">{event?.website}</span>
                  <ExternalLinkIcon
                    className="text-primary h-6 w-6 text-white"
                    aria-hidden="true"
                  />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const organizerEventDetails = () => {
    return (
      <div className="bg-white shadow rounded-md mt-4">
        <div className="px-4 py-5 sm:p-6">
          <div className="border-b-2 border-dashed border-gray-200 pb-4 mb-6">
            <div className="flex justify-between items-center ">
              <h2 className="mr-2 text-2xl text-gray-800">Details</h2>
            </div>
          </div>

          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Kursnummer:
            </div>
            <div className="font-light text-md text-gray-800">{event?.id}</div>
          </div>

          <div className={`py-1 my-3 ${event?.recurring ? 'hidden' : ''}`}>
            <div className="font-semibold text-md text-gray-800">
              Durchführungsdatum:
            </div>
            <div className="font-light text-md text-gray-800">
              {event?.additionalDates}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Veranstaltungsort:
            </div>
            <div className="font-light text-md text-gray-800">
              {event?.venue}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Region:</div>
            <div className="font-light text-md text-gray-800">
              {event?.region}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Altersstufe:
            </div>
            <div className="font-light text-md text-gray-800">
              {event?.ageRange.replace(/\|/g, ' / ')}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Anbieter:</div>
            <div className="font-light text-md text-gray-800">
              {event?.caretaker}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Kosten:</div>
            <div className="font-light text-md text-gray-800">
              {event?.price}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Kontakt:</div>
            <div className="font-light text-md text-gray-800">
              {event?.organizer}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">Auskunft:</div>
            <div className="font-light text-md text-gray-800">
              {event?.contactInformation}
            </div>
          </div>
          <div className="py-1 my-3">
            <div className="font-semibold text-md text-gray-800">
              Anmeldung:
            </div>
            <div className="font-light text-md text-gray-800 break-all">
              {event?.registrationInformation}
            </div>
          </div>
          {!!event.website && (
            <div className="py-1 my-3">
              <div className="font-semibold text-md text-gray-800">
                Website:
              </div>
              <div className="font-light text-md text-gray-800">
                <a
                  href={event.website}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center"
                >
                  <span className="mr-2 break-all">{event?.website}</span>
                  <ExternalLinkIcon
                    className="text-primary h-6 w-6 text-white"
                    aria-hidden="true"
                  />
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <NextSeo title={event?.title} description={event?.description} />
      <div className="mx-2 my-4">
        <nav
          className="flex items-start px-2 py-3 align-center"
          aria-label="Breadcrumb"
        >
          <button
            type="button"
            onClick={() => router.back()}
            className="inline-flex items-center space-x-3 text-md font-medium text-gray-900 cursor-pointer border p-2 rounded-lg shadow-md border-orange-100 opacity-75 hover:opacity-100"
          >
            <div className="bg-primary rounded-xl text-white w-6 h-6">
              <ChevronLeftIcon
                className=" h-6 w-6 text-gray-400- text-white"
                aria-hidden="true"
              />
            </div>
            <span>Zurück</span>
          </button>
        </nav>

        <section className="px-2 my-4 lg:my-8">
          <div
            className="text-2xl font-medium text-primary"
            id="test-event-title"
          >
            {event?.title}
          </div>

          <div className="py-3">
            <div
              dangerouslySetInnerHTML={{
                __html: sanitizeHTML(event?.description),
              }}
              className="text-gray-700 text-lg my-2 font-light event-card"
            />
          </div>
        </section>

        {isEventOrganizer ? organizerEventDetails() : generalEventDetails()}
      </div>
    </div>
  );
};

export async function getStaticProps({ params }: { params: { id: number } }) {
  const event = await getEventById(params.id);

  return {
    props: {
      event: event || null,
    },
  };
}

export async function getStaticPaths() {
  const allEvents = await getAllEvents();

  return {
    paths: allEvents?.map((event) => `/veranstaltungen/${event.id}`) || [],
    fallback: true,
  };
}

export default Veranstaltung;
